import clsx from 'clsx';
import { FC, useState } from 'react';
import { ShowError } from '../../../../errors/components/ShowError';
import { useGetTelegramBind } from '../../../telegram-forum/api/telegram.queries';
import { usePublishPost } from '../../posts-modal/api/posts.mutations';
import { useGetPostPublication } from '../../posts-table/api/posts.queries';

interface Props {
	isEdit: boolean;
	uuid?: string;
	checked: boolean;
	onChange: (value: boolean) => void;
	nameRu: string;
	nameEn: string;
	imagePath: string;
}

const STATUS_LABEL: Record<string, string> = {
	pending: 'Публикуется...',
	sent: 'Опубликовано',
	failed: 'Ошибка публикации',
};

/**
 * Публикация поста в Telegram: пост уходит в тему своего тега — она создаётся
 * сама, если новостей с этим тегом ещё не было.
 * Требования бэкенда: названия ru и en + обложка. Проверяем их здесь,
 * чтобы не ловить 422 после сохранения.
 */
export const TelegramPublishCard: FC<Props> = ({
	isEdit,
	uuid,
	checked,
	onChange,
	nameRu,
	nameEn,
	imagePath,
}) => {
	const [error, setError] = useState<any>();

	const { data: bind } = useGetTelegramBind();
	const { data: publication } = useGetPostPublication(isEdit ? uuid : undefined);
	const { mutate: publishNow, isLoading: isPublishing } = usePublishPost();

	const missing: string[] = [];
	if (!nameRu?.trim()) missing.push('название RU');
	if (!nameEn?.trim()) missing.push('название EN');
	if (!imagePath) missing.push('обложка');

	const isBound = Boolean(bind?.bound);
	const canPublish = isBound && missing.length === 0;

	const handlePublishNow = () => {
		if (!uuid) return;
		setError(undefined);
		publishNow(uuid, { onError: err => setError(err) });
	};

	return (
		<div className='card mb-5'>
			<div className='card-header'>
				<h3 className='card-title'>Публикация в Telegram</h3>
				{publication?.status && (
					<div className='card-toolbar'>
						<span
							className={clsx('badge fs-7', {
								'badge-light-success': publication.status === 'sent',
								'badge-light-warning': publication.status === 'pending',
								'badge-light-danger': publication.status === 'failed',
							})}
						>
							{STATUS_LABEL[publication.status] || publication.status}
						</span>
					</div>
				)}
			</div>
			<div className='card-body'>
				{!isBound && (
					<div className='alert alert-warning'>
						Telegram-группа не привязана — публикация недоступна. Привяжите
						группу в разделе <b>Лента → Telegram-группа</b>.
					</div>
				)}

				{isBound && missing.length > 0 && (
					<div className='alert alert-light-warning'>
						Для публикации не хватает: {missing.join(', ')}. В Telegram уходит
						обложка, названия на двух языках, тег, дата и ссылка на новость —
						текст поста не отправляется.
					</div>
				)}

				<div className='form-check form-switch form-check-custom form-check-solid'>
					<input
						className='form-check-input'
						type='checkbox'
						id='publish_to_telegram'
						checked={checked}
						disabled={!canPublish}
						onChange={e => onChange(e.target.checked)}
					/>
					<label className='form-check-label fw-bold' htmlFor='publish_to_telegram'>
						{isEdit && publication?.published
							? 'Обновить сообщение в Telegram при сохранении'
							: 'Опубликовать в Telegram при сохранении'}
					</label>
				</div>

				<div className='form-text mt-2'>
					Отправка идёт фоновой задачей — сообщение появится в течение минуты
					после сохранения, в теме выбранного тега.
					{isEdit && publication?.published && (
						<>
							{' '}
							Если поменять тег, сообщение переедет в тему нового тега: старое
							будет удалено и пост опубликуется заново.
						</>
					)}
				</div>

				{/* Кнопка нужна и для проваленной публикации: иначе повторить
				    отправку из админки нечем. */}
				{isEdit && publication?.status && (
					<>
						<div className='separator my-5' />
						<div className='d-flex align-items-center gap-3 flex-wrap'>
							<button
								type='button'
								className='btn btn-light-primary btn-sm'
								onClick={handlePublishNow}
								disabled={!canPublish || isPublishing}
							>
								{isPublishing
									? 'Отправляем...'
									: publication.status === 'failed'
									? 'Повторить публикацию'
									: 'Отправить в Telegram сейчас'}
							</button>
							{publication.attempts != null && publication.attempts > 1 && (
								<span className='text-muted fs-7'>
									попыток: {publication.attempts}
								</span>
							)}
						</div>
					</>
				)}

				{publication?.status === 'failed' && publication.last_error && (
					<div className='alert alert-danger mt-5 mb-0'>
						{publication.last_error}
					</div>
				)}

				{error && <ShowError error={error} />}
			</div>
		</div>
	);
};
